import {useState} from 'react';
import {format} from 'date-fns';
import {Download, History, Loader2} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {toast} from '@common-web/hooks/use-toast';
import {createCsv} from '@common-web/lib/createCsv';
import {downloadCsvFile, makeCsvFilename} from '@common-web/lib/downloadCsv';
import {cn} from '@common-web/lib/utils';
import {trpc, type RouterOutput} from '@common-web/lib/trpc';
import {
  ACTIVITY_CATEGORIES,
  ACTIVITY_CATEGORY_LABELS,
  type ActivityCategory,
} from '@common/groupPipeline/types';

type ActivityEvent = RouterOutput['internal']['groupPipeline']['activityExport'][number];

const PAGE_SIZE = 50;

// The detail page's badge calls this unfiltered, so the tab's All view shares its cache.
export function useGroupActivity(vbaGroupId: string, category: ActivityCategory | null = null) {
  return trpc.internal.groupPipeline.activity.useInfiniteQuery(
    {vbaGroupId, category, limit: PAGE_SIZE},
    {getNextPageParam: (last) => last.nextCursor, placeholderData: (previous) => previous},
  );
}

// Event timestamps are real moments, so they show in local time (unlike VBA's date-only fields).
const shortDate = (value: Date | string) => format(new Date(value), 'MMM d, yyyy');

const FILE_ACCESS_VERBS: Record<string, string> = {
  VIEWED: 'Viewed',
  OPENED: 'Opened',
  DOWNLOADED: 'Downloaded',
  PREVIEWED: 'Previewed',
};

function describe(event: ActivityEvent): string {
  const task = event.taskLabel ?? 'a step';
  const data = event.data ?? {};
  switch (event.type) {
    case 'PIPELINE_STARTED':
      return 'Started the pipeline';
    case 'PIPELINE_LIVE':
      return 'Went live';
    case 'PIPELINE_ON_HOLD':
      return 'Put on hold';
    case 'PIPELINE_RESUMED':
      return typeof data.heldDays === 'number'
        ? `Resumed after ${data.heldDays} business day${data.heldDays === 1 ? '' : 's'} on hold`
        : 'Resumed';
    case 'PIPELINE_NOT_MOVING_FORWARD':
      return 'Marked not moving forward';
    case 'PIPELINE_REINSTATED':
      return 'Reinstated';
    case 'PIPELINE_MOVED_TO_STEP':
      return typeof data.from === 'string' && data.from !== task
        ? `Moved from ${data.from} to ${task}`
        : `Moved to ${task}`;
    case 'TASK_COMPLETED':
      return `Completed ${task}`;
    case 'TASK_SKIPPED':
      return `Skipped ${task}`;
    case 'TASK_REOPENED':
      return `Reopened ${task}`;
    case 'TASK_SENT_BACK':
      return typeof data.attempt === 'number'
        ? `Sent back ${task} (attempt ${data.attempt})`
        : `Sent back ${task}`;
    case 'TASK_COMPLETED_AT_CHANGED':
      return typeof data.from === 'string' && typeof data.to === 'string'
        ? `Changed ${task}'s completed date from ${shortDate(data.from)} to ${shortDate(data.to)}`
        : `Changed ${task}'s completed date`;
    case 'TASK_RENAMED':
      return typeof data.from === 'string' ? `Renamed ${data.from} to ${task}` : `Renamed ${task}`;
    case 'TASK_DUE_DATE_CHANGED':
      return typeof data.to === 'string'
        ? `Changed ${task}'s due date to ${shortDate(data.to)}`
        : `Changed ${task}'s due date`;
    case 'TASK_ADDED':
      return `Added ${task}`;
    case 'FTP_FOLDER_LINKED':
      return typeof data.from === 'string'
        ? `Changed the FTP folder from ${data.from} to ${data.path}`
        : `Linked the FTP folder ${data.path}`;
    case 'FTP_SUBFOLDERS_UPDATED':
      return typeof data.changes === 'string'
        ? `Updated the FTP subfolders: ${data.changes}`
        : 'Updated the FTP subfolders';
    case 'DOCUMENT_FILE_LINKED':
      return typeof data.from === 'string'
        ? `Changed the ${data.document} file from ${data.from} to ${data.fileName}`
        : `Linked ${data.fileName} from the FTP as the ${data.document}`;
    case 'DOCUMENT_FILE_UPLOADED':
      return typeof data.from === 'string'
        ? `Uploaded ${data.fileName} as the ${data.document}, replacing ${data.from}`
        : `Uploaded ${data.fileName} to the FTP as the ${data.document}`;
    case 'DOCUMENT_FILE_UNLINKED':
      return `Unlinked ${data.fileName} from the ${data.document}`;
    case 'FTP_FILE_ACCESSED': {
      const verb = FILE_ACCESS_VERBS[String(data.action)] ?? 'Opened';
      if (typeof data.document === 'string') return `${verb} ${data.fileName} (${data.document})`;
      return typeof data.folder === 'string'
        ? `${verb} ${data.fileName} in ${data.folder}`
        : `${verb} ${data.fileName}`;
    }
    default:
      return event.type;
  }
}

function exportActivity(groupName: string, events: ActivityEvent[]) {
  const records = events.map((event) => ({
    Date: format(new Date(event.createdAt), 'MMM d, yyyy h:mm a'),
    'What happened': describe(event),
    Step: event.taskLabel ?? '',
    Reason: event.reason ?? '',
    By: event.actorName ?? 'System',
  }));
  const slug = groupName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  downloadCsvFile(createCsv(records), makeCsvFilename(`${slug}-activity`));
}

export function GroupActivityTab({vbaGroupId, groupName}: {vbaGroupId: string; groupName: string}) {
  const utils = trpc.useUtils();
  const [category, setCategory] = useState<ActivityCategory | null>(null);
  const [exporting, setExporting] = useState(false);
  const activity = useGroupActivity(vbaGroupId, category);
  const events = activity.data?.pages.flatMap((page) => page.events) ?? [];
  const counts = activity.data?.pages[0]?.counts;
  const total = counts?.[category ?? 'all'] ?? 0;
  // Placeholder data is the previous filter's list, kept so the page doesn't jump while loading.
  const stale = activity.isPlaceholderData;
  const pills = [
    {value: null, label: 'All', count: counts?.all},
    ...ACTIVITY_CATEGORIES.map((c) => ({
      value: c,
      label: ACTIVITY_CATEGORY_LABELS[c],
      count: counts?.[c],
    })),
  ];

  const exportFiltered = async () => {
    setExporting(true);
    try {
      const rows = await utils.internal.groupPipeline.activityExport.fetch({
        vbaGroupId,
        category,
      });
      exportActivity(groupName, rows);
    } catch {
      toast({title: 'Could not export', variant: 'destructive'});
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className='rounded-lg border bg-white p-5'>
      <div className='mb-2 flex flex-wrap items-center justify-between gap-2'>
        <h2 className='flex items-center gap-2 text-base font-semibold'>
          <History className='h-4 w-4' />
          Activity
        </h2>
        <div className='flex items-center gap-2'>
          <div role='radiogroup' aria-label='Filter activity' className='flex flex-wrap gap-1.5'>
            {pills.map((pill) => {
              const selected = pill.value === category;
              return (
                <button
                  key={pill.label}
                  type='button'
                  role='radio'
                  aria-checked={selected}
                  onClick={() => setCategory(pill.value)}
                  className={cn(
                    'flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors',
                    selected
                      ? 'border-gray-900 bg-gray-900 text-white'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50',
                  )}
                >
                  {pill.label}
                  {selected && stale ? (
                    <Loader2 className='h-3 w-3 animate-spin text-gray-300' />
                  ) : (
                    pill.count !== undefined && (
                      <span className={cn('text-xs', selected ? 'text-gray-300' : 'text-gray-500')}>
                        {pill.count}
                      </span>
                    )
                  )}
                </button>
              );
            })}
          </div>
          <Button variant='ghost' size='sm' disabled={!total || exporting} onClick={exportFiltered}>
            {exporting ? (
              <Loader2 className='mr-1 h-4 w-4 animate-spin' />
            ) : (
              <Download className='mr-1 h-4 w-4' />
            )}
            Export
          </Button>
        </div>
      </div>

      {activity.isLoading ? (
        <div className='py-10 text-center'>
          <Loader2 className='mx-auto h-5 w-5 animate-spin text-muted-foreground' />
        </div>
      ) : activity.isLoadingError ? (
        <div className='py-10 text-center'>
          <p className='mb-3 text-sm text-muted-foreground'>Couldn't load activity.</p>
          <Button
            variant='outline'
            size='sm'
            disabled={activity.isFetching}
            onClick={() => activity.refetch()}
          >
            {activity.isFetching && <Loader2 className='mr-1 h-4 w-4 animate-spin' />}
            Retry
          </Button>
        </div>
      ) : !events.length ? (
        <p className='py-10 text-center text-sm text-muted-foreground'>
          {category ? 'No matching activity.' : 'No activity yet.'}
        </p>
      ) : (
        <>
          <ul aria-busy={stale} className={cn(stale && 'pointer-events-none opacity-50')}>
            {events.map((event) => (
              <li key={event.id} className='flex gap-6 border-b py-3 last:border-0'>
                <span
                  className='w-24 shrink-0 text-sm text-gray-500'
                  title={format(new Date(event.createdAt), 'MMM d, yyyy h:mm a')}
                >
                  {shortDate(event.createdAt)}
                </span>
                <div className='min-w-0'>
                  <p className='text-sm'>
                    {describe(event)}
                    {event.reason && <span className='text-gray-600'>: {event.reason}</span>}
                  </p>
                  <p className='text-sm text-gray-500'>{event.actorName ?? 'System'}</p>
                </div>
              </li>
            ))}
          </ul>
          {!stale && (
            <div className='flex items-center justify-between border-t pt-3 text-sm text-gray-500'>
              <span>
                Showing {events.length} of {total}
              </span>
              {activity.hasNextPage && (
                <Button
                  variant='outline'
                  size='sm'
                  disabled={activity.isFetching}
                  onClick={() => activity.fetchNextPage()}
                >
                  {activity.isFetchingNextPage && <Loader2 className='mr-1 h-4 w-4 animate-spin' />}
                  {activity.isFetchNextPageError ? 'Retry' : 'Load more'}
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
